// slices/serviceProviderSearchSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ServiceProviderSearchService from "../services/ServiceProviderSearchService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

/**
 * ✅ NEW: Get all providers with pagination and optional filters
 * GET /api/service-providers/search/all-service-providers/
 */
export const getAllProviders = createAsyncThunk(
  'serviceProviderSearch/getAllProviders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getAllProviders(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Advanced search with multiple filters
 * POST /api/service-providers/search/advanced
 */
export const advancedSearch = createAsyncThunk(
  'serviceProviderSearch/advancedSearch',
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.advancedSearch(searchParams);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Search nearby providers
 * GET /api/service-providers/search/nearby
 */
export const searchNearby = createAsyncThunk(
  'serviceProviderSearch/searchNearby',
  async ({ lat, lng, radius = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.searchNearby(lat, lng, radius, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get nearby providers worldwide with automatic location detection
 * GET /api/service-providers/search/nearby-worldwide
 */
export const getNearbyProviders = createAsyncThunk(
  'serviceProviderSearch/getNearbyProviders',
  async ({ radius = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getNearbyProviders(radius, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Autocomplete search for service types, cities, or names
 * GET /api/service-providers/search/autocomplete
 */
export const autocompleteSearch = createAsyncThunk(
  'serviceProviderSearch/autocompleteSearch',
  async ({ searchTerm, type = 'service' }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.autocomplete(searchTerm, type);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get search filters (available options for filtering)
 * GET /api/service-providers/search/filters
 */
export const getSearchFilters = createAsyncThunk(
  'serviceProviderSearch/getSearchFilters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getSearchFilters();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Search by availability (for a specific day and time)
 * GET /api/service-providers/search/availability
 */
export const searchByAvailability = createAsyncThunk(
  'serviceProviderSearch/searchByAvailability',
  async ({ day, time, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.searchByAvailability(day, time, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get popular providers
 * GET /api/service-providers/search/popular
 */
export const getPopularProviders = createAsyncThunk(
  'serviceProviderSearch/getPopularProviders',
  async ({ limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getPopularProviders(limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by rating range
 * GET /api/service-providers/search/rating-range
 */
export const getByRatingRange = createAsyncThunk(
  'serviceProviderSearch/getByRatingRange',
  async ({ minRating, maxRating = 5, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getByRatingRange(minRating, maxRating, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by price range
 * GET /api/service-providers/search/price-range
 */
export const getByPriceRange = createAsyncThunk(
  'serviceProviderSearch/getByPriceRange',
  async ({ minPrice, maxPrice, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getByPriceRange(minPrice, maxPrice, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get providers by service type (shortcut)
 * GET /api/service-providers/search/type/:serviceType
 */
export const getByServiceType = createAsyncThunk(
  'serviceProviderSearch/getByServiceType',
  async ({ serviceType, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getByServiceType(serviceType, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Fuzzy search providers
 * GET /api/service-providers/search/fuzzy
 */
export const fuzzySearch = createAsyncThunk(
  'serviceProviderSearch/fuzzySearch',
  async ({ query, options = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.fuzzySearch(query, options);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get search statistics
 * GET /api/service-providers/search/statistics
 */
export const getSearchStatistics = createAsyncThunk(
  'serviceProviderSearch/getSearchStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getSearchStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- RANDOM PROVIDER ROUTES --------

/**
 * Get random providers
 * GET /api/service-providers/search/random
 */
export const getRandomProviders = createAsyncThunk(
  'serviceProviderSearch/getRandomProviders',
  async ({ limit = 10, lat = null, lng = null, unit = 'miles' }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getRandomProviders(limit, lat, lng, unit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get random providers by service type
 * GET /api/service-providers/search/random/:serviceType
 */
export const getRandomProvidersByServiceType = createAsyncThunk(
  'serviceProviderSearch/getRandomProvidersByServiceType',
  async ({ serviceType, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getRandomProvidersByServiceType(serviceType, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get random featured providers
 * GET /api/service-providers/search/random-featured
 */
export const getRandomFeaturedProviders = createAsyncThunk(
  'serviceProviderSearch/getRandomFeaturedProviders',
  async ({ limit = 5 }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.getRandomFeaturedProviders(limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PROTECTED ROUTES --------

/**
 * Clear search cache (Admin only)
 * DELETE /api/service-providers/search/cache
 */
export const clearSearchCache = createAsyncThunk(
  'serviceProviderSearch/clearSearchCache',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderSearchService.clearCache();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Search results
  results: [],
  popularProviders: [],
  autocompleteResults: [],
  nearbyProviders: [],
  availabilityResults: [],
  fuzzyResults: [],
  randomResults: [],
  
  // Search filters
  filters: null,
  searchFilters: null,
  statistics: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Current search parameters
  currentSearchParams: null,
  
  // Loading states
  loading: false,
  loadingNearby: false,
  loadingPopular: false,
  loadingFilters: false,
  loadingStatistics: false,
  loadingRandom: false,
  
  // Error states
  error: null,
  nearbyError: null,
  popularError: null,
  filtersError: null,
  randomError: null,
  
  // Cache
  cacheKey: null,
  lastSearchTimestamp: null,
  
  // UI states
  isSearching: false,
  showAdvancedSearch: false,
  selectedFilters: {},
};

const serviceProviderSearchSlice = createSlice({
  name: 'serviceProviderSearch',
  initialState,
  reducers: {
    // Reset states
    resetSearchState: (state) => {
      state.results = [];
      state.popularProviders = [];
      state.autocompleteResults = [];
      state.nearbyProviders = [];
      state.availabilityResults = [];
      state.fuzzyResults = [];
      state.randomResults = [];
      state.filters = null;
      state.searchFilters = null;
      state.statistics = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.currentSearchParams = null;
      state.error = null;
      state.nearbyError = null;
      state.popularError = null;
      state.filtersError = null;
      state.randomError = null;
      state.isSearching = false;
      state.selectedFilters = {};
    },
    
    // Clear errors
    clearSearchErrors: (state) => {
      state.error = null;
      state.nearbyError = null;
      state.popularError = null;
      state.filtersError = null;
      state.randomError = null;
    },
    
    // Set search parameters
    setSearchParams: (state, action) => {
      state.currentSearchParams = action.payload;
    },
    
    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set filters
    setFilters: (state, action) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.selectedFilters = {};
    },
    
    // Toggle advanced search
    toggleAdvancedSearch: (state) => {
      state.showAdvancedSearch = !state.showAdvancedSearch;
    },
    
    // Set loading states
    setSearching: (state, action) => {
      state.isSearching = action.payload;
    },
    
    // Add to results (for pagination)
    appendResults: (state, action) => {
      state.results = [...state.results, ...action.payload];
    },
    
    // Clear results
    clearResults: (state) => {
      state.results = [];
    },
    
    // Set cache key
    setCacheKey: (state, action) => {
      state.cacheKey = action.payload;
      state.lastSearchTimestamp = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== GET ALL PROVIDERS ==========
      .addCase(getAllProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isSearching = true;
      })
      .addCase(getAllProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.results = action.payload.data?.items || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
        state.cacheKey = action.meta.requestId;
        state.lastSearchTimestamp = Date.now();
      })
      .addCase(getAllProviders.rejected, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.error = action.payload || 'Failed to load providers';
      })
      
      // ========== ADVANCED SEARCH ==========
      .addCase(advancedSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isSearching = true;
      })
      .addCase(advancedSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.results = action.payload.data?.items || action.payload.data?.providers || action.payload.data?.results || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
        state.filters = action.payload.data?.filters || {};
        state.cacheKey = action.meta.requestId;
        state.lastSearchTimestamp = Date.now();
      })
      .addCase(advancedSearch.rejected, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.error = action.payload || 'Failed to search';
      })
      
      // ========== SEARCH NEARBY ==========
      .addCase(searchNearby.pending, (state) => {
        state.loadingNearby = true;
        state.nearbyError = null;
      })
      .addCase(searchNearby.fulfilled, (state, action) => {
        state.loadingNearby = false;
        state.nearbyProviders = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(searchNearby.rejected, (state, action) => {
        state.loadingNearby = false;
        state.nearbyError = action.payload || 'Failed to search nearby';
      })
      
      // ========== GET NEARBY PROVIDERS ==========
      .addCase(getNearbyProviders.pending, (state) => {
        state.loadingNearby = true;
        state.nearbyError = null;
      })
      .addCase(getNearbyProviders.fulfilled, (state, action) => {
        state.loadingNearby = false;
        state.nearbyProviders = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getNearbyProviders.rejected, (state, action) => {
        state.loadingNearby = false;
        state.nearbyError = action.payload || 'Failed to get nearby providers';
      })
      
      // ========== AUTOCOMPLETE ==========
      .addCase(autocompleteSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(autocompleteSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.autocompleteResults = action.payload.data || [];
      })
      .addCase(autocompleteSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get autocomplete suggestions';
      })
      
      // ========== GET SEARCH FILTERS ==========
      .addCase(getSearchFilters.pending, (state) => {
        state.loadingFilters = true;
        state.filtersError = null;
      })
      .addCase(getSearchFilters.fulfilled, (state, action) => {
        state.loadingFilters = false;
        state.searchFilters = action.payload.data;
      })
      .addCase(getSearchFilters.rejected, (state, action) => {
        state.loadingFilters = false;
        state.filtersError = action.payload || 'Failed to get search filters';
      })
      
      // ========== SEARCH BY AVAILABILITY ==========
      .addCase(searchByAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchByAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.availabilityResults = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(searchByAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to search by availability';
      })
      
      // ========== GET POPULAR PROVIDERS ==========
      .addCase(getPopularProviders.pending, (state) => {
        state.loadingPopular = true;
        state.popularError = null;
      })
      .addCase(getPopularProviders.fulfilled, (state, action) => {
        state.loadingPopular = false;
        state.popularProviders = action.payload.data || [];
      })
      .addCase(getPopularProviders.rejected, (state, action) => {
        state.loadingPopular = false;
        state.popularError = action.payload || 'Failed to get popular providers';
      })
      
      // ========== GET BY RATING RANGE ==========
      .addCase(getByRatingRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByRatingRange.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getByRatingRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get providers by rating';
      })
      
      // ========== GET BY PRICE RANGE ==========
      .addCase(getByPriceRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByPriceRange.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getByPriceRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get providers by price';
      })
      
      // ========== GET BY SERVICE TYPE ==========
      .addCase(getByServiceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByServiceType.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getByServiceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get providers by service type';
      })
      
      // ========== FUZZY SEARCH ==========
      .addCase(fuzzySearch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isSearching = true;
      })
      .addCase(fuzzySearch.fulfilled, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.fuzzyResults = action.payload.data?.items || action.payload.data?.providers || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(fuzzySearch.rejected, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.error = action.payload || 'Failed to perform fuzzy search';
      })
      
      // ========== GET SEARCH STATISTICS ==========
      .addCase(getSearchStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getSearchStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.statistics = action.payload.data;
      })
      .addCase(getSearchStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get search statistics';
      })
      
      // ========== GET RANDOM PROVIDERS ==========
      .addCase(getRandomProviders.pending, (state) => {
        state.loadingRandom = true;
        state.randomError = null;
      })
      .addCase(getRandomProviders.fulfilled, (state, action) => {
        state.loadingRandom = false;
        state.randomResults = action.payload.data || [];
      })
      .addCase(getRandomProviders.rejected, (state, action) => {
        state.loadingRandom = false;
        state.randomError = action.payload || 'Failed to get random providers';
      })
      
      // ========== GET RANDOM PROVIDERS BY SERVICE TYPE ==========
      .addCase(getRandomProvidersByServiceType.pending, (state) => {
        state.loadingRandom = true;
        state.randomError = null;
      })
      .addCase(getRandomProvidersByServiceType.fulfilled, (state, action) => {
        state.loadingRandom = false;
        state.randomResults = action.payload.data || [];
      })
      .addCase(getRandomProvidersByServiceType.rejected, (state, action) => {
        state.loadingRandom = false;
        state.randomError = action.payload || 'Failed to get random providers by service type';
      })
      
      // ========== GET RANDOM FEATURED PROVIDERS ==========
      .addCase(getRandomFeaturedProviders.pending, (state) => {
        state.loadingRandom = true;
        state.randomError = null;
      })
      .addCase(getRandomFeaturedProviders.fulfilled, (state, action) => {
        state.loadingRandom = false;
        state.randomResults = action.payload.data || [];
      })
      .addCase(getRandomFeaturedProviders.rejected, (state, action) => {
        state.loadingRandom = false;
        state.randomError = action.payload || 'Failed to get random featured providers';
      })
      
      // ========== CLEAR SEARCH CACHE ==========
      .addCase(clearSearchCache.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearSearchCache.fulfilled, (state) => {
        state.loading = false;
        state.results = [];
        state.nearbyProviders = [];
        state.popularProviders = [];
        state.autocompleteResults = [];
        state.availabilityResults = [];
        state.fuzzyResults = [];
        state.randomResults = [];
        state.cacheKey = null;
        state.lastSearchTimestamp = null;
      })
      .addCase(clearSearchCache.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to clear search cache';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectSearchState = (state) => state.serviceProviderSearch;

// Search results selectors
export const selectSearchResults = createSelector(
  selectSearchState,
  (search) => search.results
);

export const selectPopularProviders = createSelector(
  selectSearchState,
  (search) => search.popularProviders
);

export const selectAutocompleteResults = createSelector(
  selectSearchState,
  (search) => search.autocompleteResults
);

export const selectNearbyProviders = createSelector(
  selectSearchState,
  (search) => search.nearbyProviders
);

export const selectAvailabilityResults = createSelector(
  selectSearchState,
  (search) => search.availabilityResults
);

export const selectFuzzyResults = createSelector(
  selectSearchState,
  (search) => search.fuzzyResults
);

export const selectRandomResults = createSelector(
  selectSearchState,
  (search) => search.randomResults
);

// Filter selectors
export const selectSearchFilters = createSelector(
  selectSearchState,
  (search) => search.searchFilters
);

export const selectSelectedFilters = createSelector(
  selectSearchState,
  (search) => search.selectedFilters
);

export const selectStatistics = createSelector(
  selectSearchState,
  (search) => search.statistics
);

// Pagination selectors
export const selectPagination = createSelector(
  selectSearchState,
  (search) => search.pagination
);

// Loading selectors
export const selectSearchLoading = createSelector(
  selectSearchState,
  (search) => search.loading
);

export const selectNearbyLoading = createSelector(
  selectSearchState,
  (search) => search.loadingNearby
);

export const selectPopularLoading = createSelector(
  selectSearchState,
  (search) => search.loadingPopular
);

export const selectFiltersLoading = createSelector(
  selectSearchState,
  (search) => search.loadingFilters
);

export const selectStatisticsLoading = createSelector(
  selectSearchState,
  (search) => search.loadingStatistics
);

export const selectRandomLoading = createSelector(
  selectSearchState,
  (search) => search.loadingRandom
);

// Error selectors
export const selectSearchError = createSelector(
  selectSearchState,
  (search) => search.error
);

export const selectNearbyError = createSelector(
  selectSearchState,
  (search) => search.nearbyError
);

export const selectPopularError = createSelector(
  selectSearchState,
  (search) => search.popularError
);

export const selectFiltersError = createSelector(
  selectSearchState,
  (search) => search.filtersError
);

export const selectRandomError = createSelector(
  selectSearchState,
  (search) => search.randomError
);

// UI selectors
export const selectIsSearching = createSelector(
  selectSearchState,
  (search) => search.isSearching
);

export const selectShowAdvancedSearch = createSelector(
  selectSearchState,
  (search) => search.showAdvancedSearch
);

export const selectCacheKey = createSelector(
  selectSearchState,
  (search) => search.cacheKey
);

export const selectLastSearchTimestamp = createSelector(
  selectSearchState,
  (search) => search.lastSearchTimestamp
);

// Derived selectors
export const selectHasResults = createSelector(
  selectSearchResults,
  (results) => results && results.length > 0
);

export const selectResultsCount = createSelector(
  selectSearchResults,
  (results) => results.length
);

export const selectTotalResults = createSelector(
  selectPagination,
  (pagination) => pagination.total
);

export const selectHasMoreResults = createSelector(
  selectPagination,
  (pagination) => pagination.hasNext
);

export const selectCurrentPage = createSelector(
  selectPagination,
  (pagination) => pagination.page
);

export const selectTotalPages = createSelector(
  selectPagination,
  (pagination) => pagination.totalPages
);

export const selectTopRatedProviders = createSelector(
  selectSearchResults,
  (results) => {
    if (!results || results.length === 0) return [];
    return [...results]
      .filter(p => p.rating >= 4.5)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 5);
  }
);

export const selectLowestPriceProviders = createSelector(
  selectSearchResults,
  (results) => {
    if (!results || results.length === 0) return [];
    return [...results]
      .filter(p => p.hourlyRate)
      .sort((a, b) => a.hourlyRate - b.hourlyRate)
      .slice(0, 5);
  }
);

export const selectFeaturedProviders = createSelector(
  selectSearchResults,
  (results) => {
    if (!results || results.length === 0) return [];
    return results.filter(p => p.isFeatured);
  }
);

export const selectAverageRating = createSelector(
  selectSearchResults,
  (results) => {
    if (!results || results.length === 0) return 0;
    const total = results.reduce((sum, p) => sum + (p.rating || 0), 0);
    return parseFloat((total / results.length).toFixed(1));
  }
);

export const selectRandomProviders = createSelector(
  selectRandomResults,
  (results) => results || []
);

// ==================== EXPORTS ====================

export const {
  resetSearchState,
  clearSearchErrors,
  setSearchParams,
  setPagination,
  setFilters,
  clearFilters,
  toggleAdvancedSearch,
  setSearching,
  appendResults,
  clearResults,
  setCacheKey,
} = serviceProviderSearchSlice.actions;

export default serviceProviderSearchSlice.reducer;